import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMins(total) {
  if (!total) return '0:00';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthLabel(monthStr) {
  const [y, m] = monthStr.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

function prevMonthStr(monthStr) {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonthStr(monthStr) {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function currentMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function isEditableMonth(monthStr) {
  const cur = currentMonthStr();
  const prev = prevMonthStr(cur);
  return monthStr === cur || monthStr === prev;
}

function timesOverlap(s1, e1, s2, e2) {
  return s1 < e2 && e1 > s2;
}

// ── OMTimePage ────────────────────────────────────────────────────────────────

export default function OMTimePage() {
  const { user } = useAuth();

  const [myRoles, setMyRoles] = useState([]);
  const [entries, setEntries] = useState([]);
  const [viewMonth, setViewMonth] = useState(currentMonthStr());
  const [loading, setLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({ om_role_id: '', date: todayStr(), start_time: '', end_time: '', note: '' });
  const [overlapWarning, setOverlapWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/om/roles');
      const held = (res.data || []).filter(
        (r) => r.is_active && String(r.holder_user_id) === String(user?.id)
      );
      setMyRoles(held);
      if (held.length === 1 && !form.om_role_id) {
        setForm((f) => ({ ...f, om_role_id: String(held[0].id) }));
      }
    } catch { /* silent */ }
  }, [user?.id]); // eslint-disable-line

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/om/time-entries?month=${viewMonth}`);
      setEntries(res.data || []);
    } catch {
      toast.error('Fehler beim Laden der Einträge');
    } finally {
      setLoading(false);
    }
  }, [viewMonth]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Check overlap with shifts when date+times are set
  useEffect(() => {
    if (!form.date || !form.start_time || !form.end_time) { setOverlapWarning(false); return; }
    const weekStart = (() => {
      const d = new Date(form.date + 'T00:00:00');
      const day = d.getDay();
      d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      return d.toISOString().slice(0, 10);
    })();
    const weekEnd = (() => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + 6);
      return d.toISOString().slice(0, 10);
    })();
    api.get(`/shifts/calendar?start=${weekStart}&end=${weekEnd}`)
      .then((res) => {
        const shifts = (res.data || []).filter(
          (ev) => ev.extendedProps?.slot?.assigned_user_id === user?.id &&
            ev.extendedProps?.slot?.date === form.date
        );
        const overlap = shifts.some((ev) => {
          const slot = ev.extendedProps.slot;
          return timesOverlap(form.start_time, form.end_time, slot.start_time, slot.end_time);
        });
        setOverlapWarning(overlap);
      })
      .catch(() => setOverlapWarning(false));
  }, [form.date, form.start_time, form.end_time, user?.id]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.om_role_id || !form.date || !form.start_time || !form.end_time) {
      toast.error('Alle Pflichtfelder ausfüllen');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/om/time-entries', form);
      toast.success('Eintrag gespeichert');
      setForm({ om_role_id: myRoles.length === 1 ? String(myRoles[0].id) : '', date: todayStr(), start_time: '', end_time: '', note: '' });
      setOverlapWarning(false);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteEntry(id) {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await api.delete(`/om/time-entries/${id}`);
      toast.success('Gelöscht');
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler');
    }
  }

  async function saveEdit(id) {
    try {
      await api.put(`/om/time-entries/${id}`, editDraft);
      toast.success('Gespeichert');
      setEditingId(null);
      setEditDraft({});
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler');
    }
  }

  // Group entries by role for the monthly list
  const entriesByRole = {};
  for (const e of entries) {
    if (!entriesByRole[e.role_name]) entriesByRole[e.role_name] = [];
    entriesByRole[e.role_name].push(e);
  }

  const totalMins = entries.reduce((s, e) => s + (e.total_minutes || 0), 0);

  const canEdit = isEditableMonth(viewMonth);

  if (myRoles.length === 0 && !loading) {
    return (
      <div>
        <div className="page-header" style={{ marginBottom: '20px' }}>
          <div>
            <h1 className="page-title">Amtszeit</h1>
          </div>
        </div>
        <div className="card" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
          Keine Ämter zugewiesen. Wende dich an einen Admin.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title">Amtszeit</h1>
          <p className="page-subtitle">Zeit für operative Aufgaben erfassen</p>
        </div>
      </div>

      {/* Entry form */}
      <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>Amtszeit erfassen</h2>

        {overlapWarning && (
          <div style={{
            background: '#FFF3E0', border: '1px solid #FF9800', borderRadius: 'var(--radius)',
            padding: '10px 14px', marginBottom: '14px', fontSize: '0.875rem', color: '#E65100',
          }}>
            Diese Zeit überschneidet sich mit einem Schichteinsatz. Eintrag kann trotzdem gespeichert werden.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 180px' }}>
              <label className="form-label">Amt *</label>
              <select
                className="form-input"
                value={form.om_role_id}
                onChange={(e) => setForm((f) => ({ ...f, om_role_id: e.target.value }))}
                required
              >
                <option value="">— wählen —</option>
                {myRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label className="form-label">Datum *</label>
              <input
                type="date"
                className="form-input"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                required
              />
            </div>
            <div style={{ flex: '1 1 110px' }}>
              <label className="form-label">Von *</label>
              <input
                type="time"
                className="form-input"
                value={form.start_time}
                onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                required
              />
            </div>
            <div style={{ flex: '1 1 110px' }}>
              <label className="form-label">Bis *</label>
              <input
                type="time"
                className="form-input"
                value={form.end_time}
                onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                required
              />
            </div>
            <div style={{ flex: '3 1 200px' }}>
              <label className="form-label">Notiz</label>
              <input
                className="form-input"
                placeholder="Optional"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ marginTop: '14px' }}>
            <button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>

      {/* Monthly entry list */}
      <div className="card" style={{ padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Einträge</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '0.85rem' }}
              onClick={() => setViewMonth(prevMonthStr(viewMonth))}
            >‹</button>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', minWidth: '130px', textAlign: 'center' }}>
              {monthLabel(viewMonth)}
            </span>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '0.85rem' }}
              onClick={() => setViewMonth(nextMonthStr(viewMonth))}
              disabled={viewMonth >= currentMonthStr()}
            >›</button>
          </div>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '24px' }}><div className="spinner" /></div>}

        {!loading && entries.length === 0 && (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
            Keine Einträge in diesem Monat.
          </p>
        )}

        {!loading && Object.entries(entriesByRole).map(([roleName, roleEntries]) => {
          const roleTotal = roleEntries.reduce((s, e) => s + (e.total_minutes || 0), 0);
          return (
            <div key={roleName} style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '8px', color: 'var(--primary-dark)', borderBottom: '1px solid var(--border)', paddingBottom: '4px' }}>
                {roleName} — {fmtMins(roleTotal)} h
              </div>
              {roleEntries.map((e) => editingId === e.id ? (
                <div key={e.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <input type="date" className="form-input" defaultValue={e.date} onChange={(ev) => setEditDraft((d) => ({ ...d, date: ev.target.value }))} style={{ width: '130px', fontSize: '0.85rem' }} />
                  <input type="time" className="form-input" defaultValue={e.start_time} onChange={(ev) => setEditDraft((d) => ({ ...d, start_time: ev.target.value }))} style={{ width: '90px', fontSize: '0.85rem' }} />
                  <span style={{ alignSelf: 'center' }}>–</span>
                  <input type="time" className="form-input" defaultValue={e.end_time} onChange={(ev) => setEditDraft((d) => ({ ...d, end_time: ev.target.value }))} style={{ width: '90px', fontSize: '0.85rem' }} />
                  <input className="form-input" defaultValue={e.note} placeholder="Notiz" onChange={(ev) => setEditDraft((d) => ({ ...d, note: ev.target.value }))} style={{ flex: '1 1 120px', fontSize: '0.85rem' }} />
                  <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.85rem' }} onClick={() => saveEdit(e.id)}>✓</button>
                  <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }} onClick={() => { setEditingId(null); setEditDraft({}); }}>✗</button>
                </div>
              ) : (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-light)', minWidth: '90px' }}>{e.date}</span>
                  <span style={{ fontSize: '0.875rem' }}>{e.start_time?.slice(0, 5)} – {e.end_time?.slice(0, 5)}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{fmtMins(e.total_minutes)} h</span>
                  {e.note && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', flex: 1 }}>{e.note}</span>}
                  {canEdit && (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                      <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={() => { setEditingId(e.id); setEditDraft({}); }}>✎</button>
                      <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem', color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => deleteEntry(e.id)}>✕</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {!loading && entries.length > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
            <span>Gesamt</span>
            <span>{fmtMins(totalMins)} h</span>
          </div>
        )}
      </div>
    </div>
  );
}
